import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Camera, Eye, ExternalLink } from "lucide-react";

interface JobPhoto {
  id: string;
  file_name: string;
  file_path: string;
  created_at: string;
  uploaded_by: string;
}

interface JobPhotosSectionProps {
  jobId: string;
  evidenceUrls?: string[];
}

export const JobPhotosSection = ({ jobId, evidenceUrls }: JobPhotosSectionProps) => {
  const [jobPhotos, setJobPhotos] = useState<JobPhoto[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchJobPhotos();
  }, [jobId]);

  const fetchJobPhotos = async () => {
    try {
      const { data, error } = await supabase
        .from("job_photos")
        .select("*")
        .eq("job_id", jobId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setJobPhotos(data || []);
    } catch (error) {
      console.error("Error fetching job photos:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPhotoUrl = (filePath: string) => {
    const { data } = supabase.storage
      .from('job-photos')
      .getPublicUrl(filePath);
    return data.publicUrl;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Job Photos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Loading photos...</p>
        </CardContent>
      </Card>
    );
  }

  const hasJobPhotos = jobPhotos.length > 0;
  const hasEvidencePhotos = evidenceUrls && evidenceUrls.length > 0;

  if (!hasJobPhotos && !hasEvidencePhotos) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Photos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No photos available for this job.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Photos ({(jobPhotos.length || 0) + (evidenceUrls?.length || 0)})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Original Job Photos */}
          {hasJobPhotos && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="outline">Original Job Photos ({jobPhotos.length})</Badge>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {jobPhotos.map((photo) => {
                  const photoUrl = getPhotoUrl(photo.file_path);
                  return (
                    <div
                      key={photo.id}
                      className="relative group cursor-pointer border rounded-lg overflow-hidden"
                      onClick={() => setSelectedPhoto(photoUrl)}
                    >
                      <img
                        src={photoUrl}
                        alt={photo.file_name}
                        className="w-full h-24 object-cover group-hover:opacity-75 transition-opacity"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                        <Eye className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Evidence Photos from Report */}
          {hasEvidencePhotos && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="secondary">Evidence Photos ({evidenceUrls!.length})</Badge>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {evidenceUrls!.map((url, index) => (
                  <div
                    key={index}
                    className="relative group cursor-pointer border rounded-lg overflow-hidden"
                    onClick={() => setSelectedPhoto(url)}
                  >
                    <img
                      src={url}
                      alt={`Evidence photo ${index + 1}`}
                      className="w-full h-24 object-cover group-hover:opacity-75 transition-opacity"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                      <Eye className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="absolute top-1 right-1">
                      <Badge variant="destructive" className="text-xs">Evidence</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex gap-2 pt-2">
            {hasJobPhotos && (
              <Button variant="outline" size="sm" asChild>
                <a
                  href={`${window.location.origin}/job-photos?jobId=${jobId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  View All Job Photos
                </a>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Photo Viewer Dialog */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Photo Viewer</DialogTitle>
            <DialogDescription>
              Click outside the image or press escape to close
            </DialogDescription>
          </DialogHeader>
          {selectedPhoto && (
            <div className="flex justify-center">
              <img
                src={selectedPhoto}
                alt="Full size photo"
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};